/**
 * HotTable（类Excel表格）的数据校验规则。
 *
 * 同一份规则被两处复用，保证"表格里看到的红框"与"提交时被拦下的原因"完全一致：
 *   1. ./HotTable.vue —— 注入 handsontable 的列 validator，
 *      负责单元格级的即时红框高亮；
 *   2. ./HotTable.ts  —— 注入 Formily 字段 validator，
 *      负责在 form.validate() / 提交时把错误抛给表单，真正阻止提交。
 *
 * 支持的规则（均来自列配置，可在设计器的"列配置"抽屉里勾选/填写）：
 *   - required   必填：该列不允许为空。PC 端（if-web）因 minRows=1 存在内核
 *                自动补的初始行，对"全表唯一空行"豁免；移动端 minRows 恒为 0、
 *                空行必然来自用户主动新增，因此一行也不豁免
 *   - 选项范围   dropdown / autocomplete / multiselect：值必须命中 source
 *                （source 支持字符串数组与 [{label,value}] 两种写法）
 *   - numeric    必须是合法数字
 *   - date       必须符合 dateFormat（未配置格式时只做"能否解析"的宽松校验）
 */

/** 支持可选项配置的列类型 */
export const OPTION_CELL_TYPES = ['dropdown', 'autocomplete', 'multiselect'];

/** 判断单元格是否为空值 */
export function isEmptyValue(value) {
  return value === null || value === undefined || value === '';
}

/** 判断 source 是否为 [{ label, value }] 对象数组形态 */
export function isLabelValueSource(source) {
  return (
    Array.isArray(source)
    && source.length > 0
    && source.every((it) => it && typeof it === 'object' && !Array.isArray(it) && 'value' in it)
  );
}

/**
 * 取某列的合法取值集合。
 * 返回 null 表示该列不做取值范围限制（未配置 source 或不是选项类列）。
 *
 * label 与 value 都视为合法：用户在下拉里选的是 label，落库的是 value，
 * 两种形态在不同时机都可能出现在数据里。
 *
 * @param {Object} column 列配置
 * @returns {{ values: Set<string>, labels: string[] }|null}
 */
export function getOptionSet(column) {
  if (!column || OPTION_CELL_TYPES.indexOf(column.type) === -1) return null;

  const { source } = column;

  if (!Array.isArray(source) || !source.length) return null;

  const values = new Set();
  const labels = [];

  if (isLabelValueSource(source)) {
    source.forEach((item) => {
      const value = item.value === null || item.value === undefined ? '' : String(item.value);
      const label = item.label === null || item.label === undefined ? value : String(item.label);

      values.add(value);
      values.add(label);
      labels.push(label);
    });
  } else {
    source.forEach((item) => {
      const label = item === null || item === undefined ? '' : String(item);

      values.add(label);
      labels.push(label);
    });
  }

  return { values, labels };
}

/** 日期格式串（YYYY-MM-DD 之类）转成校验用的正则 */
function buildDateMatcher(dateFormat) {
  if (typeof dateFormat !== 'string' || !dateFormat.trim()) return null;

  // 先转义正则元字符，再把格式占位符替换成对应位数的数字
  const escaped = dateFormat.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = escaped
    .replace(/YYYY/g, '\\d{4}')
    .replace(/YY/g, '\\d{2}')
    .replace(/MM/g, '\\d{2}')
    .replace(/DD/g, '\\d{2}')
    .replace(/HH/g, '\\d{2}')
    .replace(/mm/g, '\\d{2}')
    .replace(/ss/g, '\\d{2}');

  return new RegExp(`^${pattern}$`);
}

/**
 * 校验单个单元格。
 *
 * @param {*} value 单元格值
 * @param {Object} column 列配置
 * @param {Object} [context] 上下文
 * @param {Boolean} [context.rowEmpty] 所在行是否为"允许跳过必填"的空行。
 *   不是单纯的"整行为空"：调用方（validateTableData / createCellValidator）
 *   把空行豁免限定在全表唯一的初始行上，用户主动新增的空行不会传 true。
 * @returns {String} 错误信息；通过校验时返回空串
 */
export function validateCellValue(value, column, context = {}) {
  if (!column || typeof column !== 'object') return '';

  if (isEmptyValue(value)) {
    // 空行豁免（context.rowEmpty）由调用方判定：仅全表唯一的初始空行跳过必填，
    // 用户主动新增的空行必填照常生效（见 validateTableData / createCellValidator）
    if (column.required && !context.rowEmpty) {
      return '不能为空';
    }

    return '';
  }

  const optionSet = getOptionSet(column);

  if (optionSet) {
    // 多选列的值可能是"A, B"这种拼接形态，逐项校验
    const parts = column.type === 'multiselect'
      ? String(value).split(',').map((item) => item.trim()).filter((item) => item !== '')
      : [String(value)];
    const invalid = parts.filter((item) => !optionSet.values.has(item));

    if (invalid.length) {
      return `"${invalid.join('、')}" 不在可选范围内`;
    }

    return '';
  }

  if (column.type === 'numeric') {
    // 允许千分位与前后空格，其余一律按非法数字处理
    const normalized = String(value).replace(/,/g, '').trim();

    if (normalized === '' || isNaN(Number(normalized))) {
      return '必须是数字';
    }

    return '';
  }

  if (column.type === 'date') {
    const matcher = buildDateMatcher(column.dateFormat);
    const text = String(value).trim();

    if (matcher) {
      if (!matcher.test(text)) {
        return `日期格式应为 ${column.dateFormat}`;
      }
    } else if (isNaN(new Date(text).getTime())) {
      return '不是合法日期';
    }

    return '';
  }

  return '';
}

/** 按列配置读取一行里某列的值（兼容对象数组模式的 'a.b' 嵌套路径与矩阵模式的下标） */
export function readCellValue(row, column, columnIndex) {
  if (row === null || row === undefined) return undefined;

  const key = column && column.data !== undefined && column.data !== ''
    ? column.data
    : columnIndex;

  if (Array.isArray(row)) {
    return row[typeof key === 'number' ? key : columnIndex];
  }

  if (typeof key !== 'string') return row[key];

  return key.split('.').reduce(
    (cursor, part) => (cursor === null || cursor === undefined ? undefined : cursor[part]),
    row,
  );
}

/** 判断一行是否所有列都为空 */
export function isRowEmpty(row, columns) {
  if (row === null || row === undefined) return true;

  return columns.every(
    (column, index) => isEmptyValue(readCellValue(row, column, index)),
  );
}

/**
 * 校验整张表。
 *
 * @param {Array} data 表格数据（对象数组或二维数组）
 * @param {Array} columns 列配置
 * @returns {Array<{ row: Number, column: Number, title: String, message: String }>}
 *          错误明细，row/column 均为从 0 开始的下标
 */
export function validateTableData(data, columns) {
  if (!Array.isArray(data) || !Array.isArray(columns) || !columns.length) return [];

  const errors = [];

  data.forEach((row, rowIndex) => {
    // 移动端 minRows 恒为 0，内核不会自动补初始行，表里的空行必然来自用户
    // 主动新增（"添加一行"/行操作面板），属于"打算填但还没填"，必填照常校验
    // （PC 端 if-web 对"全表唯一空行"豁免是因为 minRows=1 的自动补行，移动端
    // 没有这类行，一行也不豁免）。
    const rowEmpty = false;

    columns.forEach((column, columnIndex) => {
      const value = readCellValue(row, column, columnIndex);
      const message = validateCellValue(value, column, { rowEmpty });

      if (!message) return;

      errors.push({
        row: rowIndex,
        column: columnIndex,
        title: (column && (column.title || column.data)) || `第${columnIndex + 1}列`,
        message,
      });
    });
  });

  return errors;
}

/**
 * 把错误明细汇总成一条适合展示在表单项下方的文案。
 *
 * @param {Array} errors validateTableData 的返回值
 * @param {Number} [limit] 最多展示几条明细，其余折叠为"等 N 处"
 * @returns {String}
 */
export function formatValidationMessage(errors, limit = 3) {
  if (!Array.isArray(errors) || !errors.length) return '';

  const details = errors
    .slice(0, limit)
    .map((item) => `第${item.row + 1}行「${item.title}」${item.message}`)
    .join('；');

  return errors.length > limit
    ? `${details}，等 ${errors.length} 处填写有误`
    : details;
}
